import store from '@/store'
import router from '@/router'
import { ref, getCurrentInstance } from '@vue/composition-api'
import { foundryRequest } from '@/config/authConfig';
import { unparse, parse } from "papaparse";
import axios from 'axios'
import { transformRequestOptions } from '@core/utils'

export default function useSurvey() {
  // Safely grab the proxy instance once
  const instance = getCurrentInstance()
  const proxy = instance ? instance.proxy : null

  const surveyTemplate = ref({})
  const projectData = ref({})
  
  const choicesOrder = ref([
    { text: "None", value: null },
    { text: "Random", value: "random" },
    { text: "Alph. Ascending", value: "asc" },
    { text: "Alph. Descending", value: "desc" }
  ])

  const elementOptions = ref([])
  const elementLoading = ref(false)
  const languageOptions = ref([])
  const languageLoading = ref(false)
  const questionnaireCategoryOptions = ref([])
  const questionnaireCategoryLoading = ref(false)
  const questionnaireResearchMethodOptions = ref([])
  const questionnaireResearchMethodLoading = ref(false)
  const deleteQuestionnaireLoading = ref(false)
  const translationLoading = ref(false)
  const exportLoading = ref(false)
  const dlVersionLoading = ref(false)
  const generateVersionLoading = ref(false)
  const dlWordVersionLoading = ref(false)
  const questionnaireOptions = ref([])
  const questionnaireLoading = ref(false)
  const updateQuestionnaireLoading = ref(false)
  const templateOptions = ref([])
  const templateLoading = ref(false)
  const versionsItems = ref([])
  const versionsLoading = ref(false)
  
  const variableSuggestions = ref([
    { id: 1, label: "testProduct" }, { id: 2, label: "competitor" },
    { id: 3, label: "brand" }, { id: 4, label: "testCat" },
    { id: 5, label: "usageTime" }, { id: 6, label: "clutterCat1" },
    { id: 7, label: "clutterCat2" }, { id: 8, label: "clutterCat3" },
    { id: 9, label: "clutterCat4" }, { id: 10, label: "price" }
  ])

  const stimSuggestions = ref([])
  const csvDelimiter = ","
  const newLineDelimiter = "\n"

  const formatSWOFLabels = (swof) => {
    const labels =[]
    const addGroup = (key, headerName, groupName) => {
      if (swof[key]) {
        labels.push({ header: headerName })
        labels.push(...swof[key].map(item => ({ label: item.label, group: groupName })))
      }
    }
    addGroup('shelves', 'Shelves', 'Shelves')
    addGroup('skuTests', 'SKU Test', 'SKU Tests')
    addGroup('skuCompetitors', 'SKU Competitors', 'SKU Competitors')
    addGroup('lines', 'Line Tests', 'Line Tests')
    addGroup('lineCompetitors', 'Line Competitors', 'Line Competitors')
    addGroup('aidedRecall', 'Aided Recall', 'Aided Recall')
    addGroup('concepts', 'Concepts', 'Concepts')
    addGroup('custom', 'Customs', 'Customs')
    return labels
  }

  const fetchStimSuggestions = (queryParams)  => {
    return store.dispatch('app-survey/fetchStimSuggestions', queryParams)
      .then(({ data }) => {
        if (data.results.length > 0) stimSuggestions.value = formatSWOFLabels(data.results[0])
      }).catch(console.error)
  }

  const fetchElements = (queryParams)  => {
    elementLoading.value = true
    return store.dispatch('app-survey/fetchElements', queryParams)
      .then(({ data }) => {
        elementOptions.value =[{ type: "previous", name: "Previous", id: 0 }, ...data]
      })
      .catch(console.error)
      .finally(() => { elementLoading.value = false })
  }

  const fetchVersions = (queryParams) => {
    versionsLoading.value = true
    return store.dispatch('app-survey/fetchVersions', queryParams)
      .then(({ data }) => { versionsItems.value = data })
      .catch(console.error)
      .finally(() => { versionsLoading.value = false })
  }

  const fetchProject = () => {
    let params = {}
    if (router.currentRoute.params.id) params.id = router.currentRoute.params.id
    if (router.currentRoute.params.country) params.country = router.currentRoute.params.country
    if (router.currentRoute.params.templateId) params.templateId = router.currentRoute.params.templateId

    return store.dispatch('app-survey/fetchProject', params)
      .then(({ data }) => { projectData.value = data })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
  }

  const addProjectQuestionnaire = (payload)  => {
    store.dispatch('app-survey/addProjectQuestionnaire', payload)
      .then(() => {
        router.push({ name: 'survey-project-view', params: { id: payload.job_number, country: payload.country, new: true } })
      }).catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
  }

  const fetchQuestionnaires = (queryParams)  => {
    questionnaireLoading.value = true
    store.dispatch('app-survey/fetchQuestionnaires', queryParams)
      .then(({ data }) => {
        questionnaireOptions.value = flattenData(data).flat(3)
      })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { questionnaireLoading.value = false })
  }

  const flattenData = (data) => data.map(item => item.json.pages.map(section => {
    if ('elements' in section) {
      return section.elements.map(element => ({
        ...element,
        name: element.name || element.valueName,
        job: `${item.job_number} - ${item.job_name} - ${item.country}`,
        section: section.name,
        id: `${item.job_number}_${item.country}_${element.name || element.valueName}`,
        sectionDesc: section.description,
        sectionTitle: section.title,
        sectionPrefix: section.prefix
      }))
    }
    return[]
  }))

  const fetchLanguages = () => {
    languageLoading.value = true
    store.dispatch('app-survey/fetchLanguages')
      .then(({ data }) => { languageOptions.value = data })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { languageLoading.value = false })
  }

  const fetchTemplates = () => {
    templateLoading.value = true
    store.dispatch('app-survey/fetchTemplates')
      .then(({ data }) => { templateOptions.value = data })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { templateLoading.value = false })
  }

  const fetchTemplate = (id)  => {
    templateLoading.value = true
    return store.dispatch('app-survey/fetchTemplate', id)
      .then(({ data }) => { surveyTemplate.value = data })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { templateLoading.value = false })
  }

  const fetchQuestionnaireCategories = () => {
    questionnaireCategoryLoading.value = true
    return store.dispatch('app-survey/fetchQuestionnaireCategories')
      .then(({ data }) => { questionnaireCategoryOptions.value = data })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { questionnaireCategoryLoading.value = false })
  }

  const fetchQuestionnaireResearchMethods = () => {
    questionnaireResearchMethodLoading.value = true
    return store.dispatch('app-survey/fetchQuestionnaireResearchMethods')
      .then(({ data }) => { questionnaireResearchMethodOptions.value = data })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { questionnaireResearchMethodLoading.value = false })
  }

  const addQuestionnaireVersion = (payload, versions =[])  => {
    store.dispatch('app-survey/addQuestionnaireVersion', payload)
      .then(({ data }) => versions.push(data))
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
  }

  const deleteQuestionnaire = (id)  => {
    deleteQuestionnaireLoading.value = true
    store.dispatch('app-survey/deleteQuestionnaire', id)
      .then(() => { router.push({ name: 'survey-view' }) })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { deleteQuestionnaireLoading.value = false })
  }

  const updateQuestionnaire = (questionnaire)  => {
    updateQuestionnaireLoading.value = true
    store.dispatch('app-survey/updateQuestionnaire', questionnaire)
      .then(() => fetchProject())
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
      .finally(() => { updateQuestionnaireLoading.value = false })
  }

  const triggerDownload = (response) => {
    const disposition = response.headers['content-disposition']
    const filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1]
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')
    fileLink.href = fileURL
    fileLink.setAttribute('download', filename)
    document.body.appendChild(fileLink)
    fileLink.click()
    document.body.removeChild(fileLink)
  }

  const downloadQuestionnaire = (questionnaire)  => {
    exportLoading.value = true
    store.dispatch('app-survey/downloadQuestionnaire', { questionnaire })
      .then(triggerDownload)

      .catch(async error => {
        let message = error.response.statusText
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text()
            const json = JSON.parse(text)
            message = json.detail || json.error || text
          } catch (e) { /* not JSON, leave default message */ }
        }
        console.error(message)
        store.commit('comp-alert/SET_MESSAGE', message)
      })
      .finally(() => { exportLoading.value = false })
  }

  const downloadWordQuestionnaireVersion = (queryParams)  => {
    dlWordVersionLoading.value = true
    store.dispatch('app-survey/downloadWordQuestionnaireVersion', queryParams)
      .then(triggerDownload)
      .catch(error => {
        console.error(error.response)
        store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
      })
      .finally(() => { dlWordVersionLoading.value = false })
  }

  const downloadQuestionnaireVersion = (id)  => {
    dlVersionLoading.value = true
    store.dispatch('app-survey/downloadQuestionnaireVersion', id)
      .then(triggerDownload)
      .catch(error => {
        console.error(error.response)
        store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
      })
      .finally(() => { dlVersionLoading.value = false })
  }

  const updateVersion = (payload, versions)  => {
    return store.dispatch('app-survey/updateVersion', payload)
      .then(({ data }) => {
        const version = versions.find(v => v.id == data.id)
        if (version) Object.assign(version, data)
      })
      .catch(error => {
        console.error(error)
        router.push({ name: 'misc-error' })
      })
  }

  const generateQuestionnaireVersion = (id, versions, queryParams)  => {
    generateVersionLoading.value = true
    store.dispatch('app-survey/generateQuestionnaireVersion', { id, queryParams })
      .then((response) => {
        const version = versions.find(v => v.id == id)
        if (version) version.path = response.headers['x-model-data']
        triggerDownload(response)
      })
      .catch(error => {
        console.error(error.response)
        store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
      })
      .finally(() => { generateVersionLoading.value = false })
  }

  const isObjectEmpty = (obj) => obj && Object.keys(obj).length === 0 && obj.constructor === Object
  const splitIfStartsWithPrefix = (str, prefix) => str.startsWith(prefix) ? str.split(prefix)[1] : str
  const allEqual = arr => arr.every(v => v === arr[0])

  const getContentTooltip = (text, content, locale) => {
    let tooltip = ""
    if (content && text) {
      Object.keys(content).forEach(key => {
        const regex = new RegExp(`\\{${key}\\}`)
        let contentValue = content[key]
        if (Array.isArray(contentValue) && allEqual(contentValue)) contentValue = contentValue[0]
        if (contentValue?.translation) contentValue = contentValue.translation[locale]
        let currentText = typeof text === 'object' && !text.name ? text[locale] : text
        if (regex.test(currentText)) tooltip += `${key} = ${contentValue}\n`
      })
    }
    return tooltip
  }

  const listQuestionLabel = (questionnaire) => questionnaire.flatMap(page => 
    (page.elements ||[]).flatMap(element => {
      if (element.type === 'group') {
        return element.elements.map(q =>[
          splitIfStartsWithPrefix(q.name, "QX_"),
          splitIfStartsWithPrefix(q.valueName, "QX_"),
          splitIfStartsWithPrefix(element.name, "QX_")
        ])
      }
      return [[splitIfStartsWithPrefix(element.name, "QX_"), splitIfStartsWithPrefix(element.valueName, "QX_")]]
    })
  ).flat(3).filter(Boolean)

  const listQuestionElements = (questionnaire, section = null) => {
    const questions =[]
    questionnaire.forEach(page => {
      if (!section || page.name.toLowerCase() === section.toLowerCase()) {
        page.elements.forEach(element => {
          if (element.choices) {
            questions.push({
              name: element.name,
              choices: element.choicesFromQuestion ? (questions.find(el => el.name === element.choicesFromQuestion)?.choices ||[]).concat(element.choices) : element.choices
            })
          }
          if (!section && element.rows) {
            questions.push({
              name: element.name,
              choices: element.choicesFromQuestion ? (questions.find(el => el.name === element.choicesFromQuestion)?.choices ||[]).concat(element.rows) : element.rows
            })
          }
        })
      }
    })
    return questions
  }

  const textMapping = {
    title: "Title", description: "Description", text: "Text", notes: "Prgm Inst", int_notes: "Int/Rec Instructions",
    noneText: "None option text", otherText: "Other option text", html: "HTML Markup", theme: "Theme"
  }

  const questionLabels = ["ADOther1",	"ADOther2",	"ADTest",	"C_Education",	"C_Employment",	"C_Income",	"C_Marital",	"Classification",
      "concept_instruction",	"Concept_Intro",	"E_Flow_Ex_Pack_Practice",	"E_Flow_Ex_Pack1",	"E_Flow_Ex_Practice",	"E_Flow_Ex_Shelf1",
      "E_Flow_Pack_Instruction",	"E_Flow_Pack_Instruction_Practice",	"E_Flow_Pack_Practice_Instruction",	"E_Flow_Shelf_Instruction",
      "E_Flow_Shelf_Practice_Instruction",	"E_Flow_Shelf1_Instruction",	"F_Correct",	"F_DifferentiationAfterFlash",	"F_EaseofFinding",
      "F_Emotion",	"F_FindTime",	"F_Flash_Desc_Inst",	"F_Flash_descr",	"F_Flash_Inst",	"F_Flash_Inst_COMP",	"F_Flash_Inst_Diff",	"F_Flash_Inst_Practice",
      "F_Flash_Inst2",	"F_Flash_Practice",	"F_Flash_Practice_Inst",	"F_Flash_Recall",	"F_Flash1",	"F_Flash1_COMP",	"F_Flash2",	"F_FLASH3",	"F_FLASH3_COMP",
      "F_IAT",	"F_IAT_Inst",	"F_IAT_Practice",	"F_IAT_Practice_Inst",	"F_Inst",	"F_Inst2",	"FindProduct",	"Intro",	"Intro_Flash_1",	"IntroShop",
      "O_ShoppingObservation1",	"O_ShoppingObservation2",	"O_ShopTime",	"P_CategoryDriver",	"P_QuantityPurchased",	"P_ShoppingBarriers",
      "P_ShoppingMotivation",	"P_VisualCues",	"PSM_MAX",	"PSM_MIN",	"Q_Benefit",	"Q_Benefit_PostConcept",	"Q_Benefit2",	"Q_BenefitOE",
      "Q_ClickonDislike",	"Q_ClickonLike",	"Q_Communication2",	"Q_Concept_Believe",	"Q_Concept_Dislike",	"Q_Concept_Purchase_Driver",
      "Q_Consumer",	"Q_DESIGNAPPEAL",	"Q_different",	"Q_Fit_brand",	"Q_Fit_Concept",	"Q_InitialImpressions",	"Q_Meets_needs",	"Q_Missing_prod",
      "Q_Moments",	"Q_OA_4pts",	"Q_OA_Variant",	"Q_OverallLiking",	"Q_PackAtt",	"Q_PI_postconcept",	"Q_Price_Perception",	"Q_PropensityToBuy",
      "Q_PropensityToBuy_Unpriced",	"Q_ShelfPrimer",	"Q_Value",	"R_AidedRecall",	"R_UnaidedBrand",	"R_UnaidedRecall",	"rcda_Inst",	"S_Age",
      "S_Age_1",	"S_AgeChildren",	"S_BUMO",	"S_Freq_segment",	"S_Frequency",	"S_Gender",	"S_Gender_1",	"S_GroceryShop",	"S_HHsize",	"S_Industry",
      "S_Instruction",	"S_NbChildren",	"S_PastParticipation",	"S_PastPurchase_segment",	"S_PastPurchased",	"S_PXMBrand",	"S_Stimuli",
      "S2_Intro_NoAd",	"S2_Intro_WithAd",	"Shop_Instr",	"U_HomeUsage",	"U_Intro",	"U_Outro",	"U_UsageParticipation",	"Welcome","S_GenderBirth",
      "Q_OA_5pts",	"S_GenderIdentification",	"Q_DESIGNAPPEAL_6",	"C_Profession",	"C_Agglo",	"C_Ethnicity",	"C_Hispanic",	"C_Store",	"Q_PI",
      "Q_PI_variants",	"S_REJECT",	"S_TeeninHH",	"S_TeenAgreement1",	"S_TeenAgreement2",	"S_MET1",	"S_MET2",	"S_MET3",	"S_MET4",	"S_MET5",
      "Q_FitConcept",	"Q_FitBrand",	"F_FlashRange",	"S_Current_Hair_Color",	"S_Hair_Length",	"S_Hair_Texture_Natural_6pts",	"S_Hair_Texture_Natural_8pts",
      "Q_MeetsNeeds",	"Q_MissingProd",	"Q_PIPostConcept",	"P_ShoppingBarriers_OE",	"P_VisualCues_OE",	"VF_Allocation",	"VF_Pref_variant",
      "VF_Quantities",	"VF_Substitution", "F_Inst_COMP", "F_Inst2_COMP", "F_Correct_COMP","F_FindTime_COMP","F_EaseofFinding_COMP","F_Flash_Inst_COMP",
      "F_Flash1_COMP","E_Flow_Pack_Practice_Instruction_COMP","E_Flow_Ex_Pack_Practice_COMP","E_Flow_Pack_Instruction_COMP","E_Flow_Ex_Pack1_COMP","Q_Communication2_COMP",
      "Q_DBA_Intro","Q_DBA_Engagement", "Q_DBA_Attribution"
  ]


  return {
    surveyTemplate, templateOptions, templateLoading, fetchTemplates, fetchTemplate, projectData, exportLoading,
    fetchProject, addProjectQuestionnaire, downloadQuestionnaire, updateQuestionnaire, updateQuestionnaireLoading,
    deleteQuestionnaire, deleteQuestionnaireLoading, choicesOrder, getContentTooltip, textMapping, languageOptions,
    languageLoading, fetchLanguages, splitIfStartsWithPrefix, listQuestionLabel, listQuestionElements, questionLabels,
    fetchVersions, versionsItems, versionsLoading, addQuestionnaireVersion, dlVersionLoading, generateVersionLoading,
    dlWordVersionLoading, updateVersion, downloadQuestionnaireVersion, downloadWordQuestionnaireVersion,
    generateQuestionnaireVersion, fetchQuestionnaires, questionnaireLoading, questionnaireOptions,
    translationLoading, questionnaireCategoryOptions, questionnaireCategoryLoading, questionnaireResearchMethodOptions,
    questionnaireResearchMethodLoading, fetchQuestionnaireResearchMethods, fetchQuestionnaireCategories, isObjectEmpty,
    fetchStimSuggestions, stimSuggestions, fetchElements, elementLoading, elementOptions, variableSuggestions,
  }
}